// Real shipping cost in the cart (snippets/cart-shipping.liquid). Source: Shopify's Ajax shipping rates for the current
// cart and country (the same rates checkout uses). Cheapest rate = what checkout preselects. Hidden on any doubt.
(() => {
  if (window.__cartShipping) return;
  window.__cartShipping = true;
  const cache = new Map();
  const ZIP = { IT: ['81020', 'CE'], DE: ['10117', ''], AE: ['', 'DU'] }; // only where a zone needs zip/province
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  // Same money format as the theme (sample = Liquid "{{ 100 | money }}", e.g. "€1,00" or "Dhs. 1.00").
  const money = (amount, sample) => {
    const m = String(sample).match(/1([.,])00/);
    if (!m) return null;
    const dec = m[1], thou = dec === ',' ? '.' : ',';
    const [i, f] = amount.toFixed(2).split('.');
    return sample.replace(/1[.,]00/, i.replace(/\B(?=(\d{3})+(?!\d))/g, thou) + dec + f);
  };

  async function rateFor(cfg, cart) {
    const key = `${cart.token}|${cfg.cc}|${cart.item_count}|${cart.total_price}`;
    if (cache.has(key)) return cache.get(key);
    const job = (async () => {
      const [zip, province] = ZIP[cfg.cc] || ['', ''];
      const q = new URLSearchParams({ 'shipping_address[country]': cfg.cc, 'shipping_address[zip]': zip, 'shipping_address[province]': province });
      const root = cfg.root.replace(/\/$/, '');
      const prep = await fetch(`${root}/cart/prepare_shipping_rates.json?${q}`, { method: 'POST' });
      if (!prep.ok && prep.status !== 202) return null;
      for (let i = 0; i < 25; i++) {
        const res = await fetch(`${root}/cart/async_shipping_rates.json?${q}`);
        if (res.ok) {
          const j = await res.json().catch(() => null);
          if (j && Array.isArray(j.shipping_rates)) {
            if (!j.shipping_rates.length) return null;
            return j.shipping_rates.reduce((a, b) => (+b.price < +a.price ? b : a));
          }
        }
        await sleep(700);
      }
      return null;
    })().catch(() => null);
    cache.set(key, job);
    return job;
  }

  let last = null;
  // Origin of a variant (Casapulla stock or not), same source and cache as the delivery promise.
  const originOf = async (root, id) => {
    const key = `evo-origin2-${id}`;
    try { const c = sessionStorage.getItem(key); if (c) return c; } catch (e) {}
    const t = await (await fetch(`${root}/variants/${id}/?section_id=delivery-origin`)).text();
    const o = (t.match(/data-origin="(shop|service)"/) || [])[1] || 'warehouse';
    try { sessionStorage.setItem(key, o); } catch (e) {}
    return o;
  };
  // One-parcel price, same rules as the ship-once function (apps/collection-sorter/extensions/ship-once):
  // zone by country ("*" = rest), free from band.f (PL: fixed local threshold table.lf), first weight band that fits,
  // one rate per profile, bikes and kids bikes stacked by 5 (each stack priced at its heaviest bike).
  const profileRate = (t, profile, country, kg, subtotal, allowFree = true) => {
    const zones = t?.p?.[profile]; if (!zones) return null;
    const zc = (zi) => t.z?.[zi];
    const z = zones.find(([zi]) => Array.isArray(zc(zi)) && zc(zi).includes(country)) || zones.find(([zi]) => zc(zi) === '*');
    if (!z) return null; const band = t.b[z[1]]; if (!band) return null;
    if (allowFree && band.f != null && subtotal >= band.f) return 0;
    for (const [max, price] of band.w) if (kg <= max + 1e-9) return price;
    return null;
  };
  const onePrice = (t, country, lines, rate) => {
    const lf = t.lf?.[country];
    const generalLocal = lines.filter((x) => x.profile === 'general').reduce((a, x) => a + x.s, 0);
    const localOver = lf == null ? null : generalLocal >= lf - 1e-9;
    const by = {}, units = {}; let total = 0;
    for (const x of lines) {
      if (x.profile === 'bike' || x.profile === 'kids') { const u = units[x.profile] || (units[x.profile] = []); for (let i = 0; i < x.q; i++) u.push({ w: x.w / x.q, s: x.s / rate / x.q }); continue; }
      const g = by[x.profile] || (by[x.profile] = { w: 0, s: 0 }); g.w += x.w; g.s += x.s / rate;
    }
    for (const [profile, u] of Object.entries(units)) { u.sort((a, b) => b.w - a.w); for (let i = 0; i < u.length; i += 5) { const r = profileRate(t, profile, country, u[i].w, u[i].s, true); if (r == null) return null; total += r; } }
    for (const [profile, g] of Object.entries(by)) {
      const useLocal = localOver !== null && profile === 'general';
      if (useLocal && localOver) continue;
      const r = profileRate(t, profile, country, g.w, g.s, !useLocal); if (r == null) return null; total += r;
    }
    let p = total * rate; if (Math.abs(rate - 1) > 1e-9) p = Math.ceil(p - 1e-9);
    return Math.round(p * 100) / 100;
  };
  async function update() {
    const cfg = document.querySelector('[data-cart-shipping-config]')?.dataset;
    const targets = [...document.querySelectorAll('[data-cart-shipping]')];
    if (!cfg || !targets.length) return;
    let text = null;
    try {
      const root = cfg.root.replace(/\/$/, '');
      const cart = await (await fetch(`${root}/cart.js`)).json();
      const discounted = cart.items.some((i) => (i.line_level_discount_allocations || []).length || (i.discounts || []).length) || (cart.cart_level_discount_applications || []).length;
      // Mixed carts (2 parcels): Shopify's Ajax rates add one rate per origin, while checkout charges one parcel through
      // the "Spedizione unica" discount. Until that exact price is computed here, show nothing rather than a wrong number.
      let mixed = false;
      try { const g = JSON.parse(cart.attributes?._delivery_groups || 'null'); if (g && +g.parcels > 1) mixed = true; } catch (e) {}
      // Services (Montaggio completo) ship with the goods, so they never make a cart mixed.
      if (!mixed && cart.items.length > 1) mixed = new Set((await Promise.all(cart.items.map((i) => originOf(root, i.variant_id)))).filter((o) => o !== 'service')).size > 1;
      if (cart.item_count && mixed && !discounted) {
        // "Spedizione da": the default one-parcel price checkout charges ("Spedizione unica, tutto insieme").
        const table = JSON.parse(document.querySelector('[data-ship-once-rates]')?.textContent || 'null');
        const profiles = JSON.parse(document.querySelector('[data-ship-profiles]')?.textContent || '{}');
        const rate = cart.currency === cfg.shopCurrency ? 1 : parseFloat(window.Shopify?.currency?.active === cart.currency ? window.Shopify.currency.rate : NaN);
        const lines = cart.items.filter((i) => i.requires_shipping !== false).map((i) => ({ profile: profiles[i.variant_id] || 'general', q: Math.max(1, i.quantity), w: (i.grams || 0) / 1000 * i.quantity, s: i.final_line_price / 100 }));
        if (table && rate && lines.length) {
          const one = onePrice(table, cfg.cc, lines, rate);
          if (one != null) {
            const shown = one === 0 ? null : money(one, cfg.sample);
            text = one === 0 ? cfg.free : (shown ? `${cfg.from} ${shown}` + (cfg.customs === 'true' ? ` ${cfg.customsNote}` : '') : null);
          }
        }
      }
      if (cart.item_count && !discounted && !mixed) {
        const rate = await rateFor(cfg, cart);
        if (rate) {
          const eur = +rate.price;
          if (eur === 0) text = cfg.free;
          else {
            let amount = eur, cur = rate.currency || cfg.shopCurrency;
            if (cart.currency !== cur) {
              // Checkout converts with the market rate and rounds up to a whole unit (verified for AED).
              const r = parseFloat(window.Shopify?.currency?.active === cart.currency ? window.Shopify.currency.rate : NaN);
              if (!r) { text = null; throw new Error('no rate'); }
              amount = Math.ceil(eur * r); cur = cart.currency;
            }
            const shown = money(amount, cfg.sample);
            if (!shown) throw new Error('format');
            text = shown + (cfg.customs === 'true' ? ` ${cfg.customsNote}` : '');
          }
        }
      }
    } catch (e) { text = null; }
    last = text;
    // The drawer may have been re-rendered while the rates were loading: apply to the current nodes.
    document.querySelectorAll('[data-cart-shipping]').forEach((t) => {
      const row = t.closest('[data-cart-shipping-row]');
      if (text) { t.dataset.orig = t.dataset.orig ?? t.textContent; t.textContent = text; t.classList.remove('cart-summary-muted'); if (row) row.hidden = false; }
      else { if (t.dataset.orig !== undefined) t.textContent = t.dataset.orig; if (row) row.hidden = true; }
    });
  }
  let timer = null;
  const schedule = () => { clearTimeout(timer); timer = setTimeout(update, 400); };
  schedule();
  document.addEventListener('cart:updated', schedule);
  new MutationObserver((muts) => { if (muts.some((m) => [...m.addedNodes].some((n) => n.nodeType === 1 && (n.matches?.('[data-cart-shipping]') || n.querySelector?.('[data-cart-shipping]'))))) schedule(); })
    .observe(document.documentElement, { childList: true, subtree: true });
})();
